import React from 'react';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import { CustomerOption, NewCloseData, NoLedgerTransactionRecord, JournalRecord } from './types';
import { formatNumber, formatDate } from './utils';
import { ReadonlyPeriodField, ReadonlyYearField } from './CloseContextSummary';

interface Step2GeneralJournalProps {
  customers: CustomerOption[];
  newCloseData: NewCloseData;
  setNewCloseData: (data: NewCloseData) => void;
  noLedgerTransactionData: NoLedgerTransactionRecord[];
  loadingNoLedgerTransaction: boolean;
  rebuildingLedger: boolean;
  handleRebuildLedger: (e?: React.MouseEvent) => void;
  ledgerRebuildError?: string | null;
  journalData: JournalRecord[];
  loadingJournal: boolean;
  showJournalEntries: boolean;
  setShowJournalEntries: (show: boolean) => void;
  handleAddJournal: () => void;
  handleEditJournal: (journal: JournalRecord) => void;
  handleDeleteJournal: (journal: JournalRecord) => void;
  onBack?: () => void;
  onContinue?: () => void;
  loading?: boolean;
}

const Step2GeneralJournal: React.FC<Step2GeneralJournalProps> = ({
  customers,
  newCloseData,
  setNewCloseData,
  noLedgerTransactionData,
  loadingNoLedgerTransaction,
  rebuildingLedger,
  handleRebuildLedger,
  ledgerRebuildError,
  journalData,
  loadingJournal,
  showJournalEntries,
  setShowJournalEntries,
  handleAddJournal,
  handleEditJournal,
  handleDeleteJournal,
  onBack,
  onContinue,
  loading = false,
}) => {
  const customerOptions = customers.map(customer => ({
    value: customer.Oid,
    label: `${customer.CustomerName || ''} (${customer.CustomerID || ''})`
  }));

  return (
    <>
      {/* Header with buttons */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Харилцагч <span className="text-red-500">*</span>
          </label>
        </div>
        <div className="flex gap-3 ml-4">
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Буцах
            </button>
          )}
          {onContinue && (
            <button
              onClick={onContinue}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled={loading || loadingNoLedgerTransaction}
            >
              {loading || loadingNoLedgerTransaction ? 'Ажиллаж байна...' : 'Үргэлжлүүлэх'}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <SearchableDropdown
            options={customerOptions}
            value={newCloseData.CustomerOid}
            onChange={(value) => setNewCloseData({ ...newCloseData, CustomerOid: value })}
            valueLabel={customers.find(c => c.Oid === newCloseData.CustomerOid) 
              ? `${customers.find(c => c.Oid === newCloseData.CustomerOid)?.CustomerName || ''} (${customers.find(c => c.Oid === newCloseData.CustomerOid)?.CustomerID || ''})`
              : ''}
            placeholder="Харилцагч сонгох"
            className="w-full"
            enableAutoComplete={true}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReadonlyYearField yearType={newCloseData.YearType} />
          <ReadonlyPeriodField periodType={newCloseData.PeriodType} />
        </div>
      </div>

        {/* General Journal Section */}
      <div className="mt-6 border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Ерөнхий журнал</h3>
          {newCloseData.CustomerOid && newCloseData.YearType && (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Prevent multiple rapid clicks
                  if (rebuildingLedger || loadingNoLedgerTransaction) {
                    console.log('🚫 Button disabled, ignoring click');
                    return;
                  }
                  handleRebuildLedger(e);
                }}
                disabled={rebuildingLedger || loadingNoLedgerTransaction}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {rebuildingLedger ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Дахин бүтээж байна...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Гүйлгээ холбох
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        
        {/* Error message display */}
        {ledgerRebuildError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-800 font-medium">{ledgerRebuildError}</p>
            </div>
          </div>
        )}
        
        {/* Journal Entries Helper Section */}
        {newCloseData.CustomerOid && (
          <div className="mb-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-3 bg-gray-50">
              <button
                onClick={() => setShowJournalEntries(!showJournalEntries)}
                className="flex items-center justify-between flex-1 hover:bg-gray-100 transition-colors rounded"
              >
                <span className="text-sm font-medium text-gray-700">Ерөнхий журналын бичилт</span>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${showJournalEntries ? 'transform rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddJournal();
                }}
                className="ml-3 px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Нэмэх
              </button>
            </div>
            
            {showJournalEntries && (
              <div className="p-4 bg-white rounded-b-lg">
                {loadingJournal ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-600">Ерөнхий журналын бичлэгүүд уншиж байна...</p>
                  </div>
                ) : journalData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Огноо</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Тайлбар</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Дүн</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Жил</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Сар</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border border-gray-300">Үйлдэл</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {journalData.map((row, index) => (
                          <tr key={row.Oid || index} className="hover:bg-gray-50">
                            <td className="px-2 py-2 whitespace-nowrap text-gray-900 border border-gray-300">{formatDate(row.Date)}</td>
                            <td className="px-2 py-2 text-gray-900 border border-gray-300">{row.Description || '-'}</td>
                            <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.Amount)}</td>
                            <td className="px-2 py-2 text-gray-900 border border-gray-300">{row.YearType || '-'}</td>
                            <td className="px-2 py-2 text-gray-900 border border-gray-300">{row.PeriodType || '-'}</td>
                            <td className="px-2 py-2 text-center border border-gray-300">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleEditJournal(row)}
                                  className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 focus:outline-none"
                                  title="Засах"
                                >
                                  Засах
                                </button>
                                <button
                                  onClick={() => handleDeleteJournal(row)}
                                  className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 focus:outline-none"
                                  title="Устгах"
                                >
                                  Устгах
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100">
                        <tr>
                          <td colSpan={2} className="px-2 py-2 font-semibold text-gray-900 border border-gray-300">Нийт:</td>
                          <td className="px-2 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                            {formatNumber(journalData.reduce((sum, row) => sum + (row.Amount || 0), 0))}
                          </td>
                          <td colSpan={3} className="px-2 py-2 border border-gray-300"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Ерөнхий журналын бичлэг олдсонгүй
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {loadingNoLedgerTransaction ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Ерөнхий журналын мэдээлэл уншиж байна...</p>
          </div>
        ) : noLedgerTransactionData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Огноо</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Баримтын төрөл</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">РД</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Байгууллагын нэр</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Дүн</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Нийт дүн</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Бизнесийн ангилал</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {noLedgerTransactionData.map((row, index) => (
                  <tr key={row.Oid || index} className="hover:bg-gray-50">
                    <td className="px-2 py-2 whitespace-nowrap text-gray-900 border border-gray-300">{formatDate(row.Date)}</td>
                    <td className="px-2 py-2 text-gray-900 border border-gray-300">{row.ReceiptType || '-'}</td>
                    <td className="px-2 py-2 text-gray-900 border border-gray-300">{row.BusinessCustomerRd || '-'}</td>
                    <td className="px-2 py-2 text-gray-900 border border-gray-300">{row.Description || '-'}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.Amount)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.VATAmount)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.TotalAmount)}</td>
                    <td className="px-2 py-2 text-gray-900 border border-gray-300">{row.BusinessClassName || '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 sticky bottom-0">
                <tr>
                  <td colSpan={4} className="px-2 py-2 font-semibold text-gray-900 border border-gray-300">Нийт:</td>
                  <td className="px-2 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                    {formatNumber(noLedgerTransactionData.reduce((sum, row) => sum + (row.Amount || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                    {formatNumber(noLedgerTransactionData.reduce((sum, row) => sum + (row.VATAmount || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                    {formatNumber(noLedgerTransactionData.reduce((sum, row) => sum + (row.TotalAmount || 0), 0))}
                  </td>
                  <td className="px-2 py-2 border border-gray-300"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            Ерөнхий журналын мэдээлэл олдсонгүй
          </div>
        )}
      </div>
    </>
  );
};

export default Step2GeneralJournal;
